import React, { useEffect, useRef } from 'react';
import {
    Box,
    Typography,
    Checkbox,
    Divider,
    CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
    formatGiReqPlannedDisplay,
    formatInvoiceInteger,
    formatInvoiceNoQtyLine,
    getDedupedSortedInvoiceLines,
    getInvoiceLineDisplayQuantity
} from '../utils/invoiceUtils';
import {
    MobileCardItem,
    MobileCardHeader,
    MobileCardContent,
    MobileCardField,
    MobileCardLabel,
    MobileCardValue
} from '../styles';

const getCurrencySymbol = (currency) => {
    switch (currency) {
        case 'USD':
            return '$';
        case 'KRW':
            return '₩';
        case 'IDR':
            return 'Rp';
        default:
            return currency;
    }
};

const InvoiceMobileView = ({
    invoiceData,
    selectedItems,
    onSelectItem,
    onSubOrderClick,
    isInvoiceNoLoading,
    onInvoicePreviewById,
    hasMore = false,
    hasSearched = false,
    isLoading = false,
    loadingMore = false,
    onLoadMore,
    t
}) => {
    const { i18n } = useTranslation();
    const sentinelRef = useRef(null);

    useEffect(() => {
        if (!hasSearched || !hasMore || isLoading || loadingMore) return undefined;
        const sentinel = sentinelRef.current;
        if (!sentinel) return undefined;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) {
                    onLoadMore?.();
                }
            },
            { root: null, rootMargin: '200px', threshold: 0 }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasSearched, hasMore, isLoading, loadingMore, onLoadMore, invoiceData.length]);

    const formatPrice = (price) => {
        if (price === null || price === undefined || price === '') return '-';
        return parseFloat(price).toFixed(2);
    };

    const getGiReqLineQty = (row) => Number(row.gi_request_line_qty) || 0;

    const getPriceSum = (row) => {
        const q = getGiReqLineQty(row);
        if (row.price == null || row.price === '') return null;
        const p = Number(row.price);
        if (!Number.isFinite(p)) return null;
        return q * p;
    };

    return (
        <Box sx={{ mt: 2 }}>
            {invoiceData.length > 0 ? (
                <>
                {invoiceData.map((item, index) => {
                    const isSelected = selectedItems.includes(item.id);
                    const currencySymbol = getCurrencySymbol(item.price_currency);
                    const priceSum = getPriceSum(item);
                    const displayInvoiceLines = getDedupedSortedInvoiceLines(item.invoice_lines);

                    return (
                        <MobileCardItem key={item.id || index}>
                            <MobileCardHeader>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Checkbox
                                        checked={isSelected}
                                        onChange={(e) => onSelectItem(item.id, e.target.checked)}
                                        size="small"
                                    />
                                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.25 }}>
                                        <Typography variant="h6" fontWeight="bold" component="span">
                                            GI #{item.gi_request_id ?? '-'} / {t('invoice.columns.subOrderId')}{' '}
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            fontWeight="bold"
                                            component="span"
                                            onClick={() =>
                                                onSubOrderClick &&
                                                item.sub_order_id != null &&
                                                item.sub_order_id !== '' &&
                                                onSubOrderClick(item.sub_order_id)
                                            }
                                            sx={
                                                onSubOrderClick &&
                                                item.sub_order_id != null &&
                                                item.sub_order_id !== ''
                                                    ? {
                                                          color: 'primary.main',
                                                          cursor: 'pointer',
                                                          textDecoration: 'underline',
                                                          '&:hover': { color: 'primary.dark' }
                                                      }
                                                    : undefined
                                            }
                                        >
                                            {item.sub_order_id ?? '-'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </MobileCardHeader>

                            <Divider sx={{ my: 1 }} />

                            <MobileCardContent>
                                <MobileCardField>
                                    <MobileCardLabel>{t('invoice.columns.customer')}</MobileCardLabel>
                                    <MobileCardValue>{item.vendor_name || '-'}</MobileCardValue>
                                </MobileCardField>

                                <MobileCardField>
                                    <MobileCardLabel>{t('invoice.columns.style')}</MobileCardLabel>
                                    <MobileCardValue>{item.style_no ?? '-'}</MobileCardValue>
                                </MobileCardField>

                                <MobileCardField>
                                    <MobileCardLabel>{t('invoice.columns.file')}</MobileCardLabel>
                                    <MobileCardValue>{item.file_no ?? '-'}</MobileCardValue>
                                </MobileCardField>

                                <MobileCardField>
                                    <MobileCardLabel>{t('invoice.columns.dyeingNo')}</MobileCardLabel>
                                    <MobileCardValue>
                                        {item.dyeing_no != null && item.dyeing_no !== ''
                                            ? String(item.dyeing_no)
                                            : '-'}
                                    </MobileCardValue>
                                </MobileCardField>

                                <MobileCardField>
                                    <MobileCardLabel>{t('invoice.columns.delivery')}</MobileCardLabel>
                                    <MobileCardValue>{item.delivery_name || '-'}</MobileCardValue>
                                </MobileCardField>

                                <MobileCardField>
                                    <MobileCardLabel>{t('invoice.columns.item')}</MobileCardLabel>
                                    <MobileCardValue>{item.thread_name || '-'}</MobileCardValue>
                                </MobileCardField>

                                <MobileCardField>
                                    <MobileCardLabel>{t('invoice.columns.size')}</MobileCardLabel>
                                    <MobileCardValue>{item.size_name || '-'}</MobileCardValue>
                                </MobileCardField>

                                <MobileCardField>
                                    <MobileCardLabel>{t('invoice.columns.color')}</MobileCardLabel>
                                    <MobileCardValue>{item.color || '-'}</MobileCardValue>
                                </MobileCardField>

                                <MobileCardField>
                                    <MobileCardLabel>{t('invoice.columns.price')}</MobileCardLabel>
                                    <MobileCardValue>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography component="span" variant="body2" fontWeight="medium" color="primary.main" mr={0.5}>
                                                {item.price_currency ? currencySymbol : ''}
                                            </Typography>
                                            {formatPrice(item.price)}
                                        </Box>
                                    </MobileCardValue>
                                </MobileCardField>

                                <MobileCardField>
                                    <MobileCardLabel>{t('invoice.columns.orderQty')}</MobileCardLabel>
                                    <MobileCardValue>
                                        {item.order_qty != null && item.order_qty !== ''
                                            ? formatInvoiceInteger(item.order_qty, i18n.language) ?? '-'
                                            : '-'}
                                    </MobileCardValue>
                                </MobileCardField>

                                <MobileCardField>
                                    <MobileCardLabel>{t('invoice.columns.giReq')}</MobileCardLabel>
                                    <MobileCardValue>
                                        {formatGiReqPlannedDisplay(
                                            item.gi_request_planned_dispatch_at,
                                            item.gi_request_line_qty,
                                            i18n.language
                                        )}
                                    </MobileCardValue>
                                </MobileCardField>

                                <MobileCardField>
                                    <MobileCardLabel>{t('invoice.columns.packing')}</MobileCardLabel>
                                    <MobileCardValue>
                                        {Array.isArray(item.packing_lot_lines) &&
                                        item.packing_lot_lines.length > 0 ? (
                                            <Typography
                                                variant="body2"
                                                component="span"
                                                sx={{ whiteSpace: 'pre-line', display: 'block' }}
                                            >
                                                {item.packing_lot_lines
                                                    .map((ln) =>
                                                        formatGiReqPlannedDisplay(
                                                            ln.expected_gi_at,
                                                            ln.qty,
                                                            i18n.language
                                                        )
                                                    )
                                                    .join('\n')}
                                            </Typography>
                                        ) : (
                                            item.packing || '-'
                                        )}
                                    </MobileCardValue>
                                </MobileCardField>

                                <MobileCardField>
                                    <MobileCardLabel>{t('invoice.columns.giPost')}</MobileCardLabel>
                                    <MobileCardValue>{item.gi_post || '-'}</MobileCardValue>
                                </MobileCardField>

                                <MobileCardField>
                                    <MobileCardLabel>{t('invoice.columns.priceSum')}</MobileCardLabel>
                                    <MobileCardValue>
                                        {priceSum != null && Number.isFinite(priceSum) ? (
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Typography component="span" variant="body2" fontWeight="medium" color="primary.main" mr={0.5}>
                                                    {item.price_currency ? currencySymbol : ''}
                                                </Typography>
                                                {formatPrice(priceSum)}
                                            </Box>
                                        ) : (
                                            '-'
                                        )}
                                    </MobileCardValue>
                                </MobileCardField>

                                <MobileCardField>
                                    <MobileCardLabel>{t('invoice.columns.invoiceNoCol')}</MobileCardLabel>
                                    <MobileCardValue component="div">
                                        {isInvoiceNoLoading ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 0.5 }}>
                                                <CircularProgress size={18} />
                                            </Box>
                                        ) : displayInvoiceLines.length > 0 ? (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 0.35,
                                                    alignItems: 'stretch',
                                                    width: '100%'
                                                }}
                                            >
                                                {displayInvoiceLines.map((ln, idx) => {
                                                    const invId = ln.invoice_id;
                                                    const clickable =
                                                        onInvoicePreviewById &&
                                                        invId != null &&
                                                        Number.isFinite(Number(invId));
                                                    const label = formatInvoiceNoQtyLine(
                                                        ln.invoice_no,
                                                        getInvoiceLineDisplayQuantity(ln),
                                                        i18n.language
                                                    );
                                                    return clickable ? (
                                                        <Box
                                                            key={`${item.id}-m-inv-${idx}`}
                                                            component="button"
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                e.preventDefault();
                                                                onInvoicePreviewById(invId);
                                                            }}
                                                            sx={{
                                                                typography: 'body2',
                                                                width: '100%',
                                                                minHeight: 40,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                boxSizing: 'border-box',
                                                                border: 'none',
                                                                background: 'none',
                                                                padding: '6px 0',
                                                                margin: 0,
                                                                font: 'inherit',
                                                                textAlign: 'center',
                                                                cursor: 'pointer',
                                                                color: 'primary.main',
                                                                textDecoration: 'underline',
                                                                '&:hover': { color: 'primary.dark' }
                                                            }}
                                                        >
                                                            {label}
                                                        </Box>
                                                    ) : (
                                                        <Typography
                                                            key={`${item.id}-m-inv-${idx}`}
                                                            variant="body2"
                                                            component="span"
                                                            sx={{
                                                                display: 'block',
                                                                width: '100%',
                                                                textAlign: 'center'
                                                            }}
                                                        >
                                                            {label}
                                                        </Typography>
                                                    );
                                                })}
                                            </Box>
                                        ) : (
                                            item.invoice_no || item.existing_invoice_no || '-'
                                        )}
                                    </MobileCardValue>
                                </MobileCardField>
                            </MobileCardContent>
                        </MobileCardItem>
                    );
                })}
                <Box ref={sentinelRef} sx={{ height: 1 }} />
                {loadingMore && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                        <CircularProgress size={20} />
                    </Box>
                )}
                </>
            ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                        {hasSearched ? t('common.noData') : t('common.searchGuide', '검색하여 데이터를 확인하세요')}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default InvoiceMobileView;
